import React, { useState, useEffect } from 'react'
import { Card, Table, Tag, Button, Select, Input, Space, Typography, message, Modal, Grid } from 'antd'
import { useNavigate } from 'react-router-dom'
import { EyeOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons'
import { dashboardAPI, ProfilingRequest } from '../services/api'

const { Title } = Typography
const { Option } = Select
const { Search } = Input
const { useBreakpoint } = Grid

const Requests: React.FC = () => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [requests, setRequests] = useState<ProfilingRequest[]>([])
  const [filteredRequests, setFilteredRequests] = useState<ProfilingRequest[]>([])
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [searchText, setSearchText] = useState('')

  const loadRequests = async () => {
    try {
      setLoading(true)
      const data = await dashboardAPI.getRecentRequests(100, statusFilter)
      setRequests(data.requests)
      setFilteredRequests(data.requests)
    } catch (error) {
      message.error('Error al cargar las solicitudes')
      console.error('Requests load error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRequests()
  }, [statusFilter])

  useEffect(() => {
    // Filter by search text
    let filtered = requests
    
    if (searchText) {
      filtered = requests.filter(req =>
        req.simulation_id.toLowerCase().includes(searchText.toLowerCase()) ||
        req.decision_reason.toLowerCase().includes(searchText.toLowerCase()) ||
        req.customer_type?.toLowerCase().includes(searchText.toLowerCase())
      )
    }
    
    setFilteredRequests(filtered)
  }, [searchText, requests])

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return <span style={{ color: '#52c41a' }}>✓</span>
      case 'REJECTED':
        return <span style={{ color: '#ff4d4f' }}>✗</span>
      default:
        return <span style={{ color: '#faad14' }}>⏱</span>
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'success'
      case 'REJECTED':
        return 'error'
      default:
        return 'warning'
    }
  }

  const showRequestDetails = (request: ProfilingRequest) => {
    Modal.info({
      title: `Detalles de Solicitud ${request.simulation_id}`,
      width: 600,
      content: (
        <div>
          <p><strong>Estado:</strong> 
            <Tag color={getStatusColor(request.decision_status)} style={{ marginLeft: 8 }}>
              {request.decision_status}
            </Tag>
          </p>
          <p><strong>Razón de Decisión:</strong> {request.decision_reason}</p>
          <p><strong>Tipo de Cliente:</strong> {request.customer_type || 'N/A'}</p>
          <p><strong>Score Crediticio:</strong> {request.credit_score || 'N/A'}</p>
          <p><strong>Confianza IA:</strong> {request.ai_confidence ? `${(request.ai_confidence * 100).toFixed(1)}%` : 'N/A'}</p>
          <p><strong>Recomendación IA:</strong> {request.ai_recommendation || 'N/A'}</p>
          <p><strong>Fecha:</strong> {new Date(request.created_at).toLocaleString('es-ES')}</p>
        </div>
      ),
    })
  }

  const columns = [
    {
      title: 'ID Simulación',
      dataIndex: 'simulation_id',
      key: 'simulation_id',
      width: 150,
      sorter: (a: ProfilingRequest, b: ProfilingRequest) => a.simulation_id.localeCompare(b.simulation_id),
    },
    {
      title: 'Estado',
      dataIndex: 'decision_status',
      key: 'decision_status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
      filters: [
        { text: 'Aprobadas', value: 'APPROVED' },
        { text: 'Rechazadas', value: 'REJECTED' },
        { text: 'Revisión Manual', value: 'PENDING_REVIEW' },
      ],
      onFilter: (value: any, record: ProfilingRequest) => record.decision_status === value,
    },
    {
      title: 'Tipo Cliente',
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 120,
      render: (type: string) => type || 'N/A',
    },
    {
      title: 'Score',
      dataIndex: 'credit_score',
      key: 'credit_score',
      width: 100,
      render: (score: number) => score || 'N/A',
      sorter: (a: ProfilingRequest, b: ProfilingRequest) => (a.credit_score || 0) - (b.credit_score || 0),
    },
    {
      title: 'Confianza IA',
      dataIndex: 'ai_confidence',
      key: 'ai_confidence',
      width: 120,
      render: (confidence: number) => {
        if (!confidence) return 'N/A'
        const percent = (confidence * 100).toFixed(1)
        const color = confidence > 0.8 ? '#52c41a' : confidence > 0.6 ? '#faad14' : '#ff4d4f'
        return <span style={{ color }}>{percent}%</span>
      },
      sorter: (a: ProfilingRequest, b: ProfilingRequest) => (a.ai_confidence || 0) - (b.ai_confidence || 0),
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      sorter: (a: ProfilingRequest, b: ProfilingRequest) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_, record: ProfilingRequest) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => showRequestDetails(record)}
          >
            Ver
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/requests/${record.id}`)}
          >
            Detalles
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <Title level={2} style={{ margin: 0 }}>Solicitudes de Crédito</Title>
        <Button type="primary" onClick={loadRequests}>
          Actualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card style={{ marginBottom: 20 }}>
        <Space size="large" wrap>
          <div>
            <span style={{ marginRight: 8 }}>
              <FilterOutlined /> Filtrar por Estado:
            </span>
            <Select
              style={{ width: isMobile ? '100%' : 200, minWidth: 180 }}
              placeholder="Todos los estados"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="APPROVED">Aprobadas</Option>
              <Option value="REJECTED">Rechazadas</Option>
              <Option value="PENDING_REVIEW">Revisión Manual</Option>
            </Select>
          </div>
          <div>
            <span style={{ marginRight: 8 }}>
              <SearchOutlined /> Buscar:
            </span>
            <Search
              placeholder="ID simulación, razón, tipo cliente..."
              allowClear
              style={{ width: isMobile ? '100%' : 300, minWidth: 220 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </Space>
      </Card>

      {/* Tabla de solicitudes */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredRequests}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredRequests.length,
            pageSize: isMobile ? 10 : 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} solicitudes`,
          }}
          scroll={{ x: isMobile ? 1080 : 1000 }}
          size="small"
        />
      </Card>
    </div>
  )
}

export default Requests